// Back-office mock-up data: the 19 GobeletCup references shaped like the model the developers
// built on main (modele-de-donnees.md §4.2 to §4.4: product_reference → product_print_style →
// print_margin_variant + print_zone). Colours and finishes come from WooCommerce and the 3D model from the Wanadev
// team, so neither is administered here (Katia, 2026-09-24). Figures come from catalogue-references.md §1
// (Caractéristiques techniques v1.3). Nothing here is served by the backend yet.
import { EPrintStyle } from '@/typescript/enums/EPrintStyle';

// The configurator groups references by range (validated with the client on 2026-09-21).
// The target model has no column for it yet: flagged to the developers.
type TAdminRange = 'cocktail' | 'cup' | 'pitcher' | 'wine';

type TAdminPlacementKey = 'bottom' | 'center' | 'top';

// One position of a style's zone, given by its non-printable margins as the client's sheets give
// them: the margins are the data, the zone is what they leave of the developed surface (Damien's
// model on main, kept as such by Katia on 2026-10-05).
interface IAdminMarginVariant {
    bottomMm: number;
    // top / center / bottom on the four cups whose short band moves; null = a single position.
    placement: TAdminPlacementKey | null;
    // The total non-printable width: the seam in screen printing, both sides of the ECOCHOPE,
    // a gap before each face of a pitcher.
    sideMm: number;
    topMm: number;
}

// A named zone of a style: one, or the two faces of a pitcher, sharing the style's margins.
interface IAdminZoneSlot {
    key: string;
    label: string | null;
}

// Which of the full-height quadri's two zones holds for a quantity (CFG-10): the Digital one up to
// a quantity, the IML one from another, and whether the Express option forces the Digital one.
// Willy's rule of 2026-10-26 (1 to 100 copies or Express, then 150 to 5,000), administered by
// GobeletCup so that it can change without us. The quantity itself comes from WooCommerce.
interface IAdminQuantityRule {
    digitalMaxQuantity: number;
    imlMinQuantity: number;
    isExpressDigital: boolean;
}

interface IAdminPrintStyle {
    // The full-height quadri has two zones behind one option (CFG-10, Willy's table of 2026-10-26),
    // told apart here only: `marginVariants[0]` is the Digital one, with a margin above and below,
    // for a small run or the Express option; this one is the IML zone, the whole printable height,
    // from 150 copies. Absent on the other styles, and on the three smallest references.
    imlVariant?: IAdminMarginVariant;
    isActive: boolean;
    // The three smallest references have the IML zone alone: their only variant is that one.
    isImlOnly?: boolean;
    // Top to bottom when the band moves; empty when the reference does not take the style.
    marginVariants: IAdminMarginVariant[];
    maxColors: number | null;
    minDpi: number;
    // With `imlVariant`: the quantities that tell the two zones apart. On the IML-alone styles, only
    // `imlMinQuantity` is used: the copies from which the IML zone is ordered (Willy, 05/11/2026).
    quantityRule?: IAdminQuantityRule;
    style: EPrintStyle;
    zones: IAdminZoneSlot[];
}

// What WooCommerce sends about a reference. The back-office shows it and does not edit it
// (Katia, 2026-09-25): the CDC did not plan for what the shop sends. Simulated here; the real
// colour list and slugs are to come from AZApp (questions 12.1 to 12.3). Name, capacity and range
// are not in the host contract yet (contrat-hote.md §3): to be added with AZApp.
interface IAdminWooProduct {
    colors: { hex: string; name: string }[];
}

interface IAdminWooSku {
    code: string;
    isActive: boolean;
}

interface IAdminReference {
    capacityLabel: string;
    code: string;
    // Imported and kept up to date by GobeletCup in the back-office, one per colour WooCommerce
    // sends (Katia, 2026-09-25); null = not imported yet.
    colorImageUrls: Record<string, string | null>;
    developedHeightMm: number;
    developedWidthMm: number;
    isActive: boolean;
    name: { en: string; fr: string; pt: string };
    // CFG-01 physical dimensions: in no client document yet, so empty (question 1.7).
    physicalDimensionsMm: { bottomDiameter: number | null; topDiameter: number | null; totalHeight: number | null };
    printStyles: IAdminPrintStyle[];
    range: TAdminRange;
    thumbnailUrl: string;
    updatedAt: string;
    woo: IAdminWooProduct;
    // WOO-03: each association can be added, modified or deactivated.
    wooSkus: IAdminWooSku[];
}

const ADMIN_RANGE_LABELS: Record<TAdminRange, string> = {
    cup: 'Gobelets',
    cocktail: 'Verres à cocktail & chopes',
    wine: 'Verres à vin & flûtes',
    pitcher: 'Pichets',
};

const ADMIN_PRINT_STYLE_LABELS: Record<EPrintStyle, string> = {
    [EPrintStyle.MONOCOLOR]: 'Monocouleur',
    [EPrintStyle.BICOLOR]: 'Bicolore',
    [EPrintStyle.QUADRI_SMALL_HEIGHT]: 'Quadri petite hauteur',
    [EPrintStyle.QUADRI_LARGE_HEIGHT]: 'Quadri grande hauteur',
};

// Where a column or a chip has little room: the tables and the gallery cards.
const ADMIN_PRINT_STYLE_SHORT_LABELS: Record<EPrintStyle, string> = {
    [EPrintStyle.MONOCOLOR]: 'Mono',
    [EPrintStyle.BICOLOR]: 'Bi',
    [EPrintStyle.QUADRI_SMALL_HEIGHT]: 'Quadri PH',
    [EPrintStyle.QUADRI_LARGE_HEIGHT]: 'Quadri GH',
};

// The renders the configurator shows the customer for each style (client's images).
const ADMIN_PRINT_STYLE_IMAGES: Record<EPrintStyle, string> = {
    [EPrintStyle.MONOCOLOR]: '/catalog/styles/serigraphy1.png',
    [EPrintStyle.BICOLOR]: '/catalog/styles/serigraphy2.png',
    [EPrintStyle.QUADRI_SMALL_HEIGHT]: '/catalog/styles/quadriShort.png',
    [EPrintStyle.QUADRI_LARGE_HEIGHT]: '/catalog/styles/quadriTall.png',
};

const ADMIN_PRINT_STYLE_ORDER: EPrintStyle[] = [
    EPrintStyle.MONOCOLOR,
    EPrintStyle.BICOLOR,
    EPrintStyle.QUADRI_SMALL_HEIGHT,
    EPrintStyle.QUADRI_LARGE_HEIGHT,
];

// The band's positions as the client's technical sheet 1.3 names them, the CDC naming none
// (Katia, 2026-10-06: the back-office speaks the CDC's words).
const ADMIN_PLACEMENT_LABELS: Record<TAdminPlacementKey, string> = {
    top: 'Haut',
    center: 'Centre',
    bottom: 'Bas',
};

const DEFAULT_MIN_DPI = 300;

interface IAdminPlacementMargins {
    bottomMm: number;
    topMm: number;
}

interface IReferenceSeed {
    capacity: string;
    code: string;
    // ECOJAR: the zone twice, on opposite faces.
    hasTwoFaces?: boolean;
    name: string;
    // Digital (absent on ECO30, ECO50 and ECO120) and IML, as the client's table gives them.
    quadriLarge?: { digital?: { heightMm: number; topMm: number }; iml: { heightMm: number; topMm: number } };
    // `placements` = top margin at HAUT / CENTRE / BAS, on the five references that offer them;
    // `margins` = both margins of each, when the client's figures are not derived from the surface.
    quadriSmall?: {
        heightMm: number;
        margins?: [IAdminPlacementMargins, IAdminPlacementMargins, IAdminPlacementMargins];
        placements?: [number, number, number];
        topMm: number;
    };
    range: TAdminRange;
    screen: { heightMm: number; topMm: number; widthMm: number };
    surface: [number, number];
}

const SEEDS: IReferenceSeed[] = [
    {
        code: 'ECO30',
        name: 'Gobelet',
        capacity: '2/3 cl',
        range: 'cup',
        surface: [100.3, 45],
        screen: { widthMm: 85.3, heightMm: 25, topMm: 10 },
        quadriSmall: { heightMm: 25, topMm: 10 },
        quadriLarge: { iml: { heightMm: 45, topMm: 0 } },
    },
    {
        code: 'ECO50',
        name: 'Gobelet',
        capacity: '4/5 cl',
        range: 'cup',
        surface: [112.2, 59],
        screen: { widthMm: 97.2, heightMm: 39, topMm: 10 },
        quadriSmall: { heightMm: 39, topMm: 10 },
        quadriLarge: { iml: { heightMm: 59, topMm: 0 } },
    },
    {
        code: 'ECO120',
        name: 'Gobelet',
        capacity: '10/12 cl',
        range: 'cup',
        surface: [162.3, 71],
        screen: { widthMm: 147.3, heightMm: 51, topMm: 10 },
        quadriSmall: { heightMm: 51, topMm: 10 },
        quadriLarge: { iml: { heightMm: 71, topMm: 0 } },
    },
    {
        code: 'ECO180',
        name: 'Gobelet',
        capacity: '12/18 cl',
        range: 'cup',
        surface: [162.3, 96],
        screen: { widthMm: 147.3, heightMm: 71, topMm: 10 },
        quadriSmall: { heightMm: 70, topMm: 13 },
        quadriLarge: { digital: { heightMm: 76, topMm: 10 }, iml: { heightMm: 96, topMm: 0 } },
    },
    {
        code: 'ECO200',
        name: 'Gobelet',
        capacity: '20/28 cl',
        range: 'cup',
        surface: [200.5, 103],
        screen: { widthMm: 185.5, heightMm: 78, topMm: 10 },
        quadriSmall: { heightMm: 70, topMm: 10 },
        quadriLarge: { digital: { heightMm: 83, topMm: 10 }, iml: { heightMm: 103, topMm: 0 } },
    },
    {
        code: 'ECO300',
        name: 'Gobelet',
        capacity: '25/33 cl',
        range: 'cup',
        surface: [200.5, 115],
        screen: { widthMm: 185.5, heightMm: 90, topMm: 10 },
        quadriSmall: { heightMm: 70, topMm: 21 },
        quadriLarge: { digital: { heightMm: 95, topMm: 10 }, iml: { heightMm: 115, topMm: 0 } },
    },
    {
        code: 'ECO400',
        name: 'Gobelet',
        capacity: '33/40 cl',
        range: 'cup',
        surface: [198.33, 138],
        screen: { widthMm: 181.33, heightMm: 113, topMm: 10 },
        quadriSmall: { heightMm: 70, topMm: 34, placements: [12, 34, 58] },
        quadriLarge: { digital: { heightMm: 118, topMm: 10 }, iml: { heightMm: 138, topMm: 0 } },
    },
    {
        code: 'ECO450',
        name: 'Gobelet',
        capacity: '45/50 cl',
        range: 'cup',
        surface: [226.5, 140],
        screen: { widthMm: 206.5, heightMm: 115, topMm: 10 },
        quadriSmall: { heightMm: 70, topMm: 35, placements: [11, 35, 59] },
        quadriLarge: { digital: { heightMm: 120, topMm: 10 }, iml: { heightMm: 140, topMm: 0 } },
    },
    {
        code: 'ECO500',
        name: 'Gobelet',
        capacity: '50/56 cl',
        range: 'cup',
        surface: [226.5, 153],
        screen: { widthMm: 206.5, heightMm: 128, topMm: 10 },
        quadriSmall: { heightMm: 70, topMm: 41.5, placements: [14, 41.5, 73] },
        quadriLarge: { digital: { heightMm: 133, topMm: 10 }, iml: { heightMm: 153, topMm: 0 } },
    },
    {
        code: 'ECO900',
        name: 'Gobelet',
        capacity: '90/100 cl',
        range: 'cup',
        surface: [358, 140],
        screen: { widthMm: 333, heightMm: 115, topMm: 10 },
        // The client's provisional margins of 2026-10-26, as given: they are the width's, and do not fit
        // the 140 mm height (the back-office flags it); definitive ones are due in about two weeks.
        quadriSmall: {
            heightMm: 70,
            margins: [
                { topMm: 10, bottomMm: 278 },
                { topMm: 144, bottomMm: 144 },
                { topMm: 278, bottomMm: 10 },
            ],
            placements: [10, 144, 278],
            topMm: 144,
        },
        quadriLarge: { digital: { heightMm: 120, topMm: 10 }, iml: { heightMm: 140, topMm: 0 } },
    },
    {
        code: 'ECOTUBO25',
        name: 'Verre Tubo',
        capacity: '20/25 cl',
        range: 'cocktail',
        surface: [156, 144],
        screen: { widthMm: 141, heightMm: 119, topMm: 10 },
        quadriSmall: { heightMm: 70, topMm: 37, placements: [10, 37, 63] },
        quadriLarge: { digital: { heightMm: 124, topMm: 10 }, iml: { heightMm: 144, topMm: 0 } },
    },
    {
        code: 'ECOGRANITI35',
        name: 'Verre Graniti',
        capacity: '30/35 cl',
        range: 'cocktail',
        surface: [240, 112],
        screen: { widthMm: 225, heightMm: 34, topMm: 6 },
        quadriSmall: { heightMm: 29, topMm: 6 },
    },
    {
        code: 'ECOGRANITI40',
        name: 'Verre Graniti',
        capacity: '33/40 cl',
        range: 'cocktail',
        surface: [237.5, 124],
        screen: { widthMm: 222.5, heightMm: 48, topMm: 11 },
        quadriSmall: { heightMm: 40, topMm: 11 },
    },
    {
        code: 'ECOCHOPE',
        name: 'Chope',
        capacity: '50/26 cl',
        range: 'cocktail',
        surface: [224.624, 163],
        screen: { widthMm: 156.624, heightMm: 100, topMm: 10 },
    },
    {
        code: 'ECOVIN',
        name: 'Verre à vin',
        capacity: '12/18 cl',
        range: 'wine',
        surface: [178.5, 150],
        screen: { widthMm: 163.5, heightMm: 55, topMm: 10 },
        quadriSmall: { heightMm: 55, topMm: 10 },
    },
    {
        code: 'ECOVIN25',
        name: 'Verre à vin',
        capacity: '25/30 cl',
        range: 'wine',
        surface: [244, 170],
        screen: { widthMm: 229, heightMm: 40, topMm: 10 },
        quadriSmall: { heightMm: 40, topMm: 10 },
    },
    {
        code: 'ECOCHAMP',
        name: 'Flûte',
        capacity: '12/15 cl',
        range: 'wine',
        surface: [172.8, 180],
        screen: { widthMm: 157.8, heightMm: 40, topMm: 10 },
        quadriSmall: { heightMm: 40, topMm: 10 },
    },
    {
        code: 'ECOJAR100',
        name: 'Pichet',
        capacity: '1 L',
        range: 'pitcher',
        surface: [330, 167],
        screen: { widthMm: 80, heightMm: 90, topMm: 52 },
        hasTwoFaces: true,
    },
    {
        code: 'ECOJAR150',
        name: 'Pichet',
        capacity: '1,5 L',
        range: 'pitcher',
        surface: [375, 202],
        screen: { widthMm: 85, heightMm: 120, topMm: 57 },
        hasTwoFaces: true,
    },
];

function round2(valueMm: number): number {
    return Math.round(valueMm * 100) / 100;
}

const MAIN_ZONE: IAdminZoneSlot[] = [{ key: 'main', label: null }];
const FACE_ZONES: IAdminZoneSlot[] = [
    { key: 'front', label: 'Face avant' },
    { key: 'back', label: 'Face arrière' },
];

// The margins a zone of the sheet leaves: what is above it, below it, and beside it in total.
function marginVariant(
    seed: IReferenceSeed,
    zone: { heightMm: number; topMm: number; widthMm: number },
    options: { placement?: TAdminPlacementKey; zoneCount?: number } = {},
): IAdminMarginVariant {
    const [surfaceWidthMm, surfaceHeightMm] = seed.surface;
    return {
        placement: options.placement ?? null,
        topMm: zone.topMm,
        bottomMm: round2(surfaceHeightMm - zone.topMm - zone.heightMm),
        sideMm: round2(surfaceWidthMm - zone.widthMm * (options.zoneCount ?? 1)),
    };
}

function quadriStyle(style: EPrintStyle, variants: IAdminMarginVariant[]): IAdminPrintStyle {
    return {
        style,
        isActive: variants.length > 0,
        maxColors: null,
        minDpi: DEFAULT_MIN_DPI,
        marginVariants: variants,
        zones: variants.length ? structuredClone(MAIN_ZONE) : [],
    };
}

function screenStyle(seed: IReferenceSeed, style: EPrintStyle, maxColors: number): IAdminPrintStyle {
    return {
        style,
        isActive: true,
        maxColors,
        minDpi: DEFAULT_MIN_DPI,
        marginVariants: [marginVariant(seed, seed.screen, { zoneCount: seed.hasTwoFaces ? 2 : 1 })],
        zones: structuredClone(seed.hasTwoFaces ? FACE_ZONES : MAIN_ZONE),
    };
}

function buildStyles(seed: IReferenceSeed): IAdminPrintStyle[] {
    const wallWidthMm = seed.surface[0];
    const small = seed.quadriSmall;
    const band = small && { heightMm: small.heightMm, widthMm: wallWidthMm };
    let smallVariants: IAdminMarginVariant[] = [];
    if (band && small.placements) {
        const [topMm, centerMm, bottomMm] = small.placements;
        smallVariants = [
            marginVariant(seed, { ...band, topMm }, { placement: 'top' }),
            marginVariant(seed, { ...band, topMm: centerMm }, { placement: 'center' }),
            marginVariant(seed, { ...band, topMm: bottomMm }, { placement: 'bottom' }),
        ];
        small.margins?.forEach((margins, index) => {
            smallVariants[index] = { ...smallVariants[index], ...margins };
        });
    } else if (band) {
        smallVariants = [marginVariant(seed, { ...band, topMm: small.topMm })];
    }
    const large = seed.quadriLarge;
    const largeStyle = quadriStyle(
        EPrintStyle.QUADRI_LARGE_HEIGHT,
        large ? [marginVariant(seed, { ...(large.digital ?? large.iml), widthMm: wallWidthMm })] : [],
    );
    if (large?.digital) {
        largeStyle.imlVariant = marginVariant(seed, { ...large.iml, widthMm: wallWidthMm });
        largeStyle.quantityRule = { digitalMaxQuantity: 100, imlMinQuantity: 150, isExpressDigital: true };
    } else if (large) {
        largeStyle.isImlOnly = true;
        largeStyle.quantityRule = { digitalMaxQuantity: 0, imlMinQuantity: 1, isExpressDigital: false };
    }
    return [
        screenStyle(seed, EPrintStyle.MONOCOLOR, 1),
        screenStyle(seed, EPrintStyle.BICOLOR, 2),
        quadriStyle(EPrintStyle.QUADRI_SMALL_HEIGHT, smallVariants),
        largeStyle,
    ];
}

// A style switched on for a reference whose sheet gives it no zone (ADM-02: every style can be
// offered on every reference, client's review of 2026-10-12): it starts from the reference's
// screen-printing zone, its height and its faces, and is adjusted in the Zones tab. Quadri prints
// all around a single-face cup, so its side margin starts at 0 there.
function startingZoneOf(reference: IAdminReference, style: EPrintStyle): Pick<IAdminPrintStyle, 'marginVariants' | 'zones'> {
    const screen = reference.printStyles.find((candidate) => candidate.style === EPrintStyle.MONOCOLOR);
    const isQuadri = style === EPrintStyle.QUADRI_SMALL_HEIGHT || style === EPrintStyle.QUADRI_LARGE_HEIGHT;
    // Copied slot by slot: the reference is reactive by then, and structuredClone refuses a proxy.
    const zones = (screen?.zones.length ? screen.zones : MAIN_ZONE).map((zone) => ({ ...zone }));
    const variant = screen?.marginVariants[0];
    if (!variant) {
        return { marginVariants: [{ bottomMm: 0, placement: null, sideMm: 0, topMm: 0 }], zones };
    }
    const sideMm = isQuadri && zones.length === 1 ? 0 : variant.sideMm;
    return { marginVariants: [{ ...variant, placement: null, sideMm }], zones };
}

const UPDATED_AT = ['2026-09-23', '2026-09-22', '2026-09-18', '2026-09-15'];

// Named the way the client validated them on 2026-10-01: the colour says how solid it is.
const CUP_COLORS = [
    { name: 'Blanc opaque', hex: '#FFFFFF' },
    { name: 'Givré translucide', hex: '#E6EEEF' },
    { name: 'Noir opaque', hex: '#1F1F1F' },
    { name: 'Bleu opaque', hex: '#2B7BD3' },
    { name: 'Fuchsia opaque', hex: '#D3167E' },
    { name: 'Orange opaque', hex: '#F28C28' },
    { name: 'Rouge opaque', hex: '#D93025' },
    { name: 'Vert opaque', hex: '#7BC043' },
];
const YELLOW = { name: 'Jaune opaque', hex: '#F5C400' };

// The client's renders are named after the colour: `ECO300-BL.png` is the blue one, the
// unsuffixed file the frosted one.
const COLOR_FILE_SUFFIX: Record<string, string> = {
    'Blanc opaque': 'BA',
    'Givré translucide': '',
    'Noir opaque': 'N',
    'Bleu opaque': 'BL',
    'Fuchsia opaque': 'F',
    'Orange opaque': 'O',
    'Rouge opaque': 'R',
    'Vert opaque': 'V',
    'Jaune opaque': 'J',
};

function buildColorImages(seed: IReferenceSeed, woo: IAdminWooProduct): Record<string, string | null> {
    return Object.fromEntries(
        woo.colors.map((color) => {
            const suffix = COLOR_FILE_SUFFIX[color.name];
            // One colour left without an image, so the mock-up shows the "to import" state.
            const isMissing = seed.code === 'ECO300' && color.name === YELLOW.name;
            return [color.name, isMissing || suffix === undefined ? null : `/catalog/${seed.code}${suffix ? `-${suffix}` : ''}.png`];
        }),
    );
}

function buildWooProduct(seed: IReferenceSeed): IAdminWooProduct {
    if (seed.range === 'pitcher') {
        return { colors: [CUP_COLORS[1]] };
    }
    if (seed.range === 'wine') {
        return { colors: CUP_COLORS.slice(0, 3) };
    }
    const colors = seed.code === 'ECO300' ? [...CUP_COLORS, YELLOW] : [...CUP_COLORS];
    return { colors };
}

const ADMIN_REFERENCES: IAdminReference[] = SEEDS.map((seed, index) => ({
    code: seed.code,
    colorImageUrls: buildColorImages(seed, buildWooProduct(seed)),
    name: { fr: `${seed.name} ${seed.capacity}`, en: '', pt: '' },
    capacityLabel: seed.capacity,
    range: seed.range,
    developedWidthMm: seed.surface[0],
    developedHeightMm: seed.surface[1],
    physicalDimensionsMm: { totalHeight: null, topDiameter: null, bottomDiameter: null },
    isActive: true,
    printStyles: buildStyles(seed),
    thumbnailUrl: `/catalog/${seed.code}.png`,
    updatedAt: UPDATED_AT[index % UPDATED_AT.length],
    woo: buildWooProduct(seed),
    wooSkus: [{ code: `GC-${seed.code}`, isActive: true }],
}));

export {
    ADMIN_PLACEMENT_LABELS,
    ADMIN_PRINT_STYLE_IMAGES,
    ADMIN_PRINT_STYLE_LABELS,
    ADMIN_PRINT_STYLE_ORDER,
    ADMIN_PRINT_STYLE_SHORT_LABELS,
    ADMIN_RANGE_LABELS,
    ADMIN_REFERENCES,
    startingZoneOf,
};

export type { IAdminMarginVariant, IAdminPrintStyle, IAdminQuantityRule, IAdminReference, TAdminPlacementKey, TAdminRange };
